import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { extractCountryAndCity, formatMetric } from '../../../../utils/util';
import {
  ACTIVE_LISTING,
  ARCHIVED_LISTING,
  COPY_LISTING,
  COPY_LISTING_DRAFT,
  DRAFT_LISTING,
  NOT_SURE,
  PENDING_LISTING,
} from '../../../../utils/constants';

import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import TextInput from '../../../common/inputs/text-input/TextInput';
import DatePicker from '../../../common/inputs/date-picker/DatePicker';
import FilePicker from '../../../common/inputs/file-picker/FilerPicker';
import Button from '../../../common/button/Button';
import Image from '../../../common/image/Image';
import ImageCarousel from '../../../common/image-carousel/ImageCarousel';

import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';

import './upload-buyer-listing.scss';

const UploadBuyerListing = ({
  listing,
  classifications,
  categories,
  types,
  metricUnits,
  retrieveCategories,
  retrieveTypes,
  retrieveMetricUnits,
  createBuyerListing,
  updateBuyerListing,
  copyListing,
  currencyCode,
}) => {
  const [classification, setClassification] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [brandName, setBrandName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [metric, setMetric] = useState(undefined);
  const [price, setPrice] = useState('');
  const [buyingPeriodFrom, setBuyingPeriodFrom] = useState(undefined);
  const [buyingPeriodTo, setBuyingPeriodTo] = useState(undefined);
  const [originCountry, setOriginCountry] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [images, setImages] = useState([]);
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const fileRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!listing) return;

    const { classification } = listing;
    setClassification(classification);
    retrieveCategories(classification.externalReference);
  }, [listing, retrieveCategories]);

  useEffect(() => {
    if (!listing) return;

    const { category } = listing;
    setCategory(category);
    retrieveTypes(category.externalReference);
  }, [listing, retrieveTypes]);

  useEffect(() => {
    if (!listing) return;

    const { specification } = listing;
    setType(specification);
    retrieveMetricUnits(specification.externalReference);
  }, [listing, retrieveMetricUnits]);

  useEffect(() => {
    if (!listing) return;

    setQuantity(listing.desiredQuantity ?? '');
    setMetric({ label: listing.desiredQuantityMetric, value: listing.desiredQuantityMetric });

    setBrandName(listing.brandName ?? '');
    setPrice(parseFloat(listing.preferredPrice?.split(' ')[0] ?? '').toFixed(2));
    setBuyingPeriodFrom(listing.preferredBuyingPeriodFrom ? new Date(listing.preferredBuyingPeriodFrom) : undefined);
    setBuyingPeriodTo(listing.preferredBuyingPeriodTo ? new Date(listing.preferredBuyingPeriodTo) : undefined);

    const [originCountry, originCity] = extractCountryAndCity(listing.preferredSourceLocation);
    setOriginCountry(originCountry);
    setOriginCity(originCity);

    const [destinationCountry, destinationCity] = extractCountryAndCity(listing.destinationLocation);
    setDestinationCountry(destinationCountry);
    setDestinationCity(destinationCity);

    setImages(listing.linkedMediaExternalReferences ?? []);
    setAdditionalInformation(listing.additionalInformation ?? '');
  }, [listing]);

  const handleClassificationChange = (classification) => {
    setClassification(classification);
    retrieveCategories(classification.externalReference);

    if (classification.externalReference === NOT_SURE) {
      handleCategoryChange(classification);
      return;
    }

    setCategory(null);
    setType(null);
    setMetric(null);
  };

  const handleCategoryChange = (category) => {
    setCategory(category);
    retrieveTypes(category.externalReference);

    if (category.externalReference === NOT_SURE) {
      handleTypeChange(category);
      return;
    }

    setType(null);
    setMetric(null);
  };

  const handleTypeChange = (type) => {
    setType(type);
    retrieveMetricUnits(type.externalReference);
    setMetric(null);
  };

  const handleFileChange = (file) => {
    setImages([...images, file]);
  };

  const handleDeleteImageClick = (event, index) => {
    event.stopPropagation();
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const validateFields = () => {
    const errors = {};

    if (!classification) errors.classification = true;
    if (!category) errors.category = true;
    if (!type) errors.type = true;
    if (!quantity) errors.quantity = true;
    if (!metric) errors.metric = true;
    if (!price) errors.price = true;
    if (!buyingPeriodFrom) errors.buyingPeriodFrom = true;
    if (!buyingPeriodTo) errors.buyingPeriodTo = true;
    if (!destinationCountry) errors.destinationCountry = true;
    if (!destinationCity) errors.destinationCity = true;

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = (status) => {
    const valid = validateFields();
    if (!valid) return;

    let productSpecificationExternalReference = null;

    if (type.externalReference !== NOT_SURE) {
      productSpecificationExternalReference = type.externalReference;
    }

    const buyerListing = {
      desiredQuantity: quantity,
      desiredQuantityMetric: metric?.value,
      desiredPricePerProductUnit: price,
      preferredBuyingPeriodFrom: buyingPeriodFrom,
      preferredBuyingPeriodTo: buyingPeriodTo,
      preferredOriginCountry: originCountry,
      preferredOriginCity: originCity,
      photos: images,
      productClassificationName: classification.name,
      productCategoryName: category.name,
      productSpecificationName: type.name,
      productSpecificationExternalReference,
      destinationCountry,
      destinationCity,
      brandName,
      additionalInformation,
      status,
    };

    if (!listing) {
      createBuyerListing(buyerListing);
    } else if (listing && status === COPY_LISTING) {
      buyerListing.status = PENDING_LISTING;
      copyListing(buyerListing);
    } else if (listing && status === COPY_LISTING_DRAFT) {
      buyerListing.status = DRAFT_LISTING;
      copyListing(buyerListing);
    } else {
      updateBuyerListing(buyerListing);
    }
  };

  const renderImages = () => {
    return images.map((image, index) => {
      const key = typeof image === 'string' ? image : image.name;
      return (
        <div
          key={key}
          className="upload-buyer-listing__form__photo-container"
          onClick={() => setSelectedImageIndex(index)}
        >
          {typeof image === 'string' ? (
            <Image className="upload-buyer-listing__form__photo" externalReference={image} alt="" />
          ) : (
            <Image className="upload-buyer-listing__form__photo" file={image} alt="" />
          )}
          <TrashIcon onClick={(event) => handleDeleteImageClick(event, index)} data-testid="delete-image-button" />
        </div>
      );
    });
  };

  const renderPrimaryButton = () => {
    let text;
    let status;

    if (!listing) {
      text = 'Upload';
      status = PENDING_LISTING;
    } else if (listing && listing.status === DRAFT_LISTING) {
      text = 'Activate Listing';
      status = PENDING_LISTING;
    } else if (listing && listing.status === ARCHIVED_LISTING) {
      text = 'Copy and Renew Listing';
      status = COPY_LISTING;
    } else {
      text = 'Update';
    }

    return <Button onClick={() => handleSaveClick(status)}>{text}</Button>;
  };

  const renderSecondaryButton = () => {
    let text;
    let status;

    if (listing && listing.status === DRAFT_LISTING) {
      text = 'Update';
      status = DRAFT_LISTING;
    } else if (listing && listing.status === ACTIVE_LISTING) {
      text = 'Archive';
      status = ARCHIVED_LISTING;
    } else if (listing && listing.status === ARCHIVED_LISTING) {
      text = 'Copy as Draft';
      status = COPY_LISTING_DRAFT;
    } else {
      text = 'Save as Draft';
      status = DRAFT_LISTING;
    }

    return (
      <Button onClick={() => handleSaveClick(status)} secondary>
        {text}
      </Button>
    );
  };

  return (
    <>
      <section className="upload-buyer-listing" data-testid="upload-buyer-listing">
        <section className="upload-buyer-listing__card">
          <h1>Upload Buyer Listing</h1>
          <section className="upload-buyer-listing__form">
            <p>Product Details</p>

            <label htmlFor="product-classification">Product Classification</label>
            <Dropdown
              name="product-classification"
              value={classification}
              options={classifications}
              onChange={handleClassificationChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.classification}
              noSort
            />

            <label htmlFor="product-category">Product Category</label>
            <Dropdown
              name="product-category"
              value={category}
              options={categories}
              onChange={handleCategoryChange}
              disabled={!classification}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.category}
              noSort
            />

            <label htmlFor="product-type">Product Type</label>
            <Dropdown
              name="product-type"
              value={type}
              options={types}
              onChange={handleTypeChange}
              disabled={!category}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.type}
              noSort
            />

            <label htmlFor="product-name">Product Brand Name</label>
            <TextInput
              name="product-name"
              placeholder="Leave blank if product is generic"
              value={brandName}
              onChange={({ target }) => setBrandName(target.value)}
            />

            <section className="upload-buyer-listing__form__grid">
              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-quantity">Desired Quantity is</label>
                <div>
                  <TextInput
                    name="product-quantity"
                    value={quantity}
                    onChange={({ target }) => setQuantity(target.value)}
                    error={errors.quantity}
                    numberOnly
                  />
                  <Dropdown
                    value={metric}
                    options={metricUnits}
                    onChange={(option) => setMetric(option)}
                    disabled={!type}
                    error={errors.metric}
                  />
                </div>
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-price">Desired Price per Product Unit is</label>
                <div>
                  <TextInput
                    name="product-price"
                    value={price}
                    onChange={({ target }) => setPrice(target.value)}
                    error={errors.price}
                    numberOnly
                  />
                  <p>
                    {currencyCode}
                    {metric ? ` per ${formatMetric(metric?.label, 1)}.` : '.'}
                  </p>
                </div>
                {currencyCode !== 'USD' && (
                  <div className="upload-buyer-listing__price-disclaimer">
                    <p className="upload-buyer-listing__price-disclaimer__text">*</p>
                    <p className="upload-buyer-listing__price-disclaimer__text">
                      Please note that while you have selected to view all prices in {currencyCode}, all prices are
                      recorded in USD as per the exchange rate at the time of uploading this buyer listing.
                    </p>
                  </div>
                )}
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-buying-period-from">Preferred Buying Period From</label>
                <DatePicker
                  name="product-buying-period-from"
                  value={buyingPeriodFrom}
                  onChange={(date) => setBuyingPeriodFrom(date)}
                  error={errors.buyingPeriodFrom}
                  maxDate={buyingPeriodTo}
                />
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-buying-period-to">Preferred Buying Period To</label>
                <DatePicker
                  name="product-buying-period-to"
                  value={buyingPeriodTo}
                  onChange={(date) => setBuyingPeriodTo(date)}
                  error={errors.buyingPeriodTo}
                  minDate={buyingPeriodFrom}
                />
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-origin-country">Preferred Origin Country</label>
                <TextInput
                  name="product-origin-country"
                  value={originCountry}
                  onChange={({ target }) => setOriginCountry(target.value)}
                />
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-origin-city">Preferred Origin City</label>
                <TextInput
                  name="product-origin-city"
                  value={originCity}
                  onChange={({ target }) => setOriginCity(target.value)}
                />
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-destination-country">Destination Country</label>
                <TextInput
                  name="product-destination-country"
                  value={destinationCountry}
                  onChange={({ target }) => setDestinationCountry(target.value)}
                  error={errors.destinationCountry}
                />
              </section>

              <section className="upload-buyer-listing__form__grid__item">
                <label htmlFor="product-destination-city">Destination City</label>
                <TextInput
                  name="product-destination-city"
                  value={destinationCity}
                  onChange={({ target }) => setDestinationCity(target.value)}
                  error={errors.destinationCity}
                />
              </section>
            </section>

            <section className="upload-buyer-listing__form__photos">
              <p>Photos</p>
              <div>
                {renderImages()}
                <FilePicker
                  fileRef={fileRef}
                  enableFileLimit={true}
                  fileType="image/*"
                  onChange={({ target }) => handleFileChange(target.files[0])}
                  triggerElement={
                    <button
                      className="upload-buyer-listing__form__file-button"
                      onClick={() => fileRef.current.click()}
                      data-testid="add-image-button"
                    />
                  }
                />
              </div>
            </section>

            <section className="upload-buyer-listing__form__grid__item">
              <label htmlFor="product-information">Additional Information</label>
              <TextInput
                name="product-information"
                value={additionalInformation}
                onChange={({ target }) => setAdditionalInformation(target.value)}
                multiline
              />
            </section>

            <section className="upload-buyer-listing__form__actions">
              <Button onClick={() => navigate(-1)} secondary>
                Cancel
              </Button>
              {renderSecondaryButton()}
              {renderPrimaryButton()}
            </section>
          </section>
        </section>
      </section>

      {selectedImageIndex !== undefined && (
        <ImageCarousel images={images} startIndex={selectedImageIndex} setStartIndex={setSelectedImageIndex} />
      )}
    </>
  );
};

UploadBuyerListing.propTypes = {
  listing: PropTypes.shape({}),
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),
  metricUnits: PropTypes.arrayOf(PropTypes.shape({})),
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  retrieveMetricUnits: PropTypes.func.isRequired,
  createBuyerListing: PropTypes.func.isRequired,
  updateBuyerListing: PropTypes.func.isRequired,
  copyListing: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

UploadBuyerListing.defaultProps = {
  listing: undefined,
  classifications: [],
  categories: [],
  types: [],
  metricUnits: [],
};

export default UploadBuyerListing;
